<template>
  <div class="ti-vehicle-data" v-if="step.data">
    <Transition name="fade" mode="out-in">
      <FormComponent
        class="ti-vehicle-data__form"
        :key="step.stepId"
        v-if="step.form"
        :form="step.form"
        :updating="updating"
        @formChanged="formChanged"
        @modalClosed="modalClosed"
      />
    </Transition>

    <NotificationComponent
      v-if="notification && notification.message"
      :notification="notification"
      :currentStepId="step.stepId"
      @closeNotification="closeNotification"
    />

    <NavigationComponent
      v-if="
        step.subStepId !== 'mesearch' ||
        (step.subStepId === 'mesearch' &&
          step.uavList &&
          step.uavList.length > 0)
      "
      class="ti-vehicle-data__navigation"
      :step="step"
      :history="history"
      :completed="loading"
      @nextClicked="nextClicked"
      @prevClicked="prevClicked"
    />
  </div>
</template>
<script lang="ts" setup>
import { Ref, ref, watch } from 'vue';
import { Form, Step } from '@tradein/api';
import { Notification, StepIds } from '@tradein/store';
import { UserAssociatedVehicles } from '@owc/connection-wko-manager';
import { checkStepFormValid } from '../../components/form/utils';
import { trackingService } from '../../utils/tracking';
import { getFormElementByVariant } from '../../components/form/utils';
import FormComponent from '../../components/form/form.vue';
import NavigationComponent from '../../components/navigation/navigation.vue';
import NotificationComponent from '../../components/notification/notification.vue';

interface Props {
  step: Step;
  history: Step[];
  loading: boolean;
  updating: boolean;
  notification: Notification;
}

const props = defineProps<Props>();
const emit = defineEmits([
  'stepChanged',
  'stepChangedAndNext',
  'nextClicked',
  'prevClicked',
  'closeNotification',
  'getStep',
]);

watch(
  () => props.step?.subStepId,
  () => {
    trackingService.publishClick(`feature`, `${props.step?.subStepId}.switch`);
  },
);

const formChanged = (form: Form): void => {
  // TODO: Check why timeout is needed and refactor it
  setTimeout(() => {
    props.step.completed = checkStepFormValid(props.step);
    props.step.form = form;
    emit('stepChanged', props.step);
  }, 0);
};

const nextClicked = (): void => {
  emit('nextClicked', { delay: 1000 });

  trackingService.publishClick(
    `feature`,
    `${props.step.stepId === StepIds.vehicledata ? props.step.subStepId : props.step.stepId}.selected`
      .replace('modelsearch', 'modelselection')
      .replace('vinsearch', 'vinidentification'),
  );
};

const prevClicked = (): void => {
  emit('prevClicked');
};

const closeNotification = (): void => {
  emit('closeNotification');
};

const modalClosed = (): void => {
  if (props.step.form) {
    const formElement = getFormElementByVariant(props.step.form, 'Tapbar');
    formElement.field.selectedValue = 'modelSelection';
    emit('stepChanged', props.step);
  }

  trackingService.publishClick(`feature`, `vin_not_available`);
};

const selectedUserAssociatedVehicle = (
  vehicle: UserAssociatedVehicles,
): void => {
  props.step.uav = {
    description: vehicle.modelDescription,
    imageUrl: vehicle.imageUrl,
    mileage: vehicle.mileage.value,
    vin: vehicle.finOrVin,
  };

  props.step.completed = true;

  emit('stepChangedAndNext', props.step);

  trackingService.publishClick(`feature`, `${props.step.stepId}.selected`);
};
</script>
<style lang="scss">
.ti-vehicle-data {
  width: 100%;

  &__choices {
    display: flex;
    flex-flow: row wrap;
    margin-top: -8px;
    margin-bottom: 24px;
  }

  &__choice {
    @include wb-type-copy;
    position: relative;
    color: var(--wb-grey-20);
    padding: 4px 16px 4px 36px;
    border-radius: 18px;
    border: 1px solid var(--wb-grey-70);
    cursor: pointer;
    margin-right: 8px;
    margin-top: 8px;

    &:last-of-type {
      margin-right: 0;
    }

    &::before {
      content: '';
      position: absolute;
      width: 12px;
      height: 12px;
      background-color: var(--wb-white);
      border: 1px solid var(--wb-grey-70);
      top: 50%;
      left: 12px;
      transform: translateY(-50%);
      border-radius: 50%;
    }

    &:hover {
      background: var(--wb-grey-85);
      border-color: var(--wb-grey-70);
    }

    &--selected {
      color: var(--wb-white);
      background: var(--wb-black);
      border: 1px solid var(--wb-black);

      &::before {
        border-color: unset;
      }

      &:hover {
        background: var(--wb-grey-40);
        border-color: var(--wb-grey-40);
      }
    }
  }

  &__form {
    &.fade-enter-active,
    &.fade-leave-active {
      transition: opacity 0.3s $wb-fade;
    }

    &.fade-enter-from,
    &.fade-leave-to {
      opacity: 0;
    }
  }
}
</style>
